// How each ending case is shown, the same everywhere it appears (the
// Ending cards, the review's chip), so order, names, explanations, colours
// and library filters cannot drift apart. Order: around a pie, along a
// stacked bar and in legends: won, then the lost chances on one red ramp
// (slipped deepest, missed, lost on time), then a plain loss and a draw.
// Colours are this package's tokens (colors.css). `filter` is the `blunder`
// value that lists exactly a case's games; won and drawn have none (no
// filter isolates the games that reached an ending).
import type { EndingCase, GameBlunder } from '@chess/api-model';

export interface EndingCaseView {
  case: EndingCase;
  label: string;
  /** A colour token from colors.css, e.g. `var(--viz-ending-slipped)`. */
  colour: string;
  /** One sentence on the case, from the reader's side: the tooltip wherever a single game's case is shown. */
  description: string;
  filter?: GameBlunder;
}

export const ENDING_CASE_VIEWS: readonly EndingCaseView[] = [
  { case: 'won', label: 'Won', colour: 'var(--viz-ending-won)', description: 'You reached an ending and won the game.' },
  { case: 'slipped', label: 'Slipped', colour: 'var(--viz-ending-slipped)', description: 'You stood winning in the ending and didn’t win.', filter: 'ending-slipped' },
  { case: 'missed', label: 'Missed', colour: 'var(--viz-ending-missed)', description: 'You had a winning move in the ending, didn’t play it, and didn’t win.', filter: 'ending-missed' },
  { case: 'time', label: 'Lost on time', colour: 'var(--viz-ending-time)', description: 'You lost on time in the ending.', filter: 'ending-time' },
  { case: 'lost', label: 'Lost', colour: 'var(--viz-ending-lost)', description: 'You lost the ending without having had a winning chance in it.', filter: 'ending-lost' },
  { case: 'drawn', label: 'Drawn', colour: 'var(--viz-ending-drawn)', description: 'The ending was drawn, with no winning chance for you in it.' },
];

/** The view of one case. */
export function endingCaseView(c: EndingCase): EndingCaseView {
  return ENDING_CASE_VIEWS.find((v) => v.case === c)!;
}

/** The cases that lost a winning chance in the ending: the points an ending card ranks by. */
export const LOST_CHANCES: readonly EndingCase[] = ['slipped', 'missed', 'time'];

/** The cases of the endings the reader lost or let slip, in order: the "Your losses" card's slices (a plain draw is not one). */
export const LOSSES: readonly EndingCase[] = ['slipped', 'missed', 'time', 'lost'];
